import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "cpi_admin_token";
const EMAIL_KEY = "cpi_admin_email";

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getEmail: () => localStorage.getItem(EMAIL_KEY),
  setSession: (token, email) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, email);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
  },
  isAuthed: () => !!localStorage.getItem(TOKEN_KEY),
};

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const t = auth.getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && auth.isAuthed()) {
      auth.clear();
      if (window.location.pathname.startsWith("/admin")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  },
);

/**
 * postWithRetry — POST a JSON body (or FormData if config.headers is set) and
 * automatically retry on transient network failures / 5xx status codes. Does
 * NOT retry on 4xx (validation/auth errors).
 *
 * Designed for the preview environment which can cold-start after idle.
 * Also useful on production for one-off Cloudflare edge blips.
 *
 * Options:
 *   maxAttempts    default 3
 *   retryDelaysMs  default [0, 4000, 10000]  (first attempt no wait)
 *   onRetry(attemptNumber, err)  callback fired BEFORE each retry attempt
 *   headers        forwarded to axios (e.g. multipart)
 */
export async function postWithRetry(path, data, opts = {}) {
  const {
    maxAttempts = 3,
    retryDelaysMs = [0, 4000, 10000],
    onRetry,
    headers,
  } = opts;
  let lastErr;
  for (let i = 0; i < maxAttempts; i++) {
    if (retryDelaysMs[i] > 0) {
      await new Promise((r) => setTimeout(r, retryDelaysMs[i]));
    }
    try {
      return await api.post(path, data, headers ? { headers } : undefined);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Retry only on network errors (no response) or 5xx server errors.
      // 4xx (validation, auth, not found, etc.) are NOT retryable.
      const isRetryable = !err.response || (typeof status === "number" && status >= 500 && status < 600);
      const isLast = i >= maxAttempts - 1;
      if (!isRetryable || isLast) break;
      onRetry?.(i + 1, err);
    }
  }
  throw lastErr;
}
